import { motion } from "framer-motion";
import { MessageCircle, Search, CheckCircle } from "lucide-react";

const HowItWorksSection = () => {
  const steps = [
    {
      icon: MessageCircle,
      number: "01",
      title: "Text MyLane on WhatsApp",
      description: "Tell us where you are and when you're available. No apps to download, no accounts to create.",
      color: "bg-green-500",
    },
    {
      icon: Search,
      number: "02",
      title: "Get the Best Loads",
      description: "MyLane scans multiple load boards and finds the highest paying loads for your lane.",
      color: "bg-accent",
    },
    {
      icon: CheckCircle,
      number: "03",
      title: "Approve and Drive",
      description: "We negotiate the rate and prepare all the paperwork. You just approve and hit the road.",
      color: "bg-primary",
    },
  ];

  return (
    <section
      id="how-it-works"
      className="py-20 md:py-32 bg-slate-50"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
            Simple Process
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary tracking-tight uppercase mb-6">
            How It Works
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
            Getting started with MyLane is as easy as sending a text message.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 relative">
          {/* Connection line - desktop only */}
          <div className="hidden md:block absolute top-24 left-1/6 right-1/6 h-0.5 bg-slate-200 z-0" />
          
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              className="relative z-10"
              data-testid={`step-${index + 1}`}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 h-full">
                {/* Step number */}
                <div className="flex items-center justify-between mb-6">
                  <div className={`w-16 h-16 rounded-xl ${step.color} flex items-center justify-center`}>
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <span className="font-heading text-5xl font-bold text-slate-100">
                    {step.number}
                  </span>
                </div>
                
                <h3 className="font-heading text-2xl font-bold text-primary mb-4">
                  {step.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
              
              {/* Arrow for mobile */}
              {index < steps.length - 1 && (
                <div className="md:hidden flex justify-center my-4">
                  <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* App Context Image */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-16 relative rounded-2xl overflow-hidden"
        >
          <img
            src="https://images.unsplash.com/photo-1699435660785-ef774f7ccb35?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
            alt="Driver using smartphone in truck cab"
            className="w-full h-64 md:h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent flex items-end">
            <div className="p-8 text-white">
              <p className="font-heading text-2xl md:text-3xl font-bold uppercase mb-2">
                Dispatch From Your Cab
              </p>
              <p className="text-slate-200 max-w-lg">
                No need to call, email, or use complicated apps. Just text MyLane like you'd text a friend.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
